import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';

export type ApprovalEntry = {
  type: string;
  createdAt: string;
};

export type ApprovalResponse = {
  taskId: string;
  approved: boolean;
  createdAt: string;
  approvals: ApprovalEntry[];
};

export interface ApprovalApi {
  getApproval(taskId: string): Promise<ApprovalResponse | undefined>;
  approveAsRole(taskId: string): Promise<ApprovalResponse | undefined>;
  approveWithCode(
    taskId: string,
    code: string,
  ): Promise<ApprovalResponse | undefined>;
}

export const approvalApiRef = createApiRef<ApprovalApi>().with({
  id: 'plugin.scaffolder.request-approval.api',
});

export class ApprovalClient implements ApprovalApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getApproval(taskId: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/approval/${encodeURIComponent(taskId)}`,
    );

    if (response.status === 404) return undefined;

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Failed to fetch approval with status ${response.status}: ${body}`,
      );
    }

    return response.json() as Promise<ApprovalResponse>;
  }

  async approveAsRole(taskId: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/approval/${encodeURIComponent(taskId)}/as-role`,
      { method: 'POST' },
    );

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Failed to approve request with status ${response.status}: ${body}`,
      );
    }

    return undefined;
  }

  async approveWithCode(taskId: string, code: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/approval/${encodeURIComponent(taskId)}/with-code`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      },
    );

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Failed to approve request with status ${response.status}: ${body}`,
      );
    }

    return undefined;
  }
}

export const approvalApi = ApiBlueprint.make({
  name: 'request-approval',
  params: defineParams =>
    defineParams({
      api: approvalApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new ApprovalClient(discoveryApi, fetchApi),
    }),
});
